import { FC, useEffect, useRef, useState } from "react";
import dayjs from 'dayjs';
import { fetchAsync, postAsync } from "../../services/ApiHandler";
import { getToken, isLoggedIn } from "../../services/CookieHandler";
import { GenerationRequest } from "../../model/GenerationRequest";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import { ComboboxItem, Loader, Select } from "@mantine/core";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { FooterMenu } from "../../components/FooterMenu";
import DownloadIcon from "../../assets/icon/download.png";
import TrashIcon from "../../assets/icon/icons8-trash-96.png";
import iconImageAdd from "../../img/icon-image-add.png";
import bananaIcon from "../../img/banana-icon.png";

type ConfirmActionState = {
  type: 'delete' | 'download';
  ids: string[];
  origin: 'selection' | 'detail';
};

type GeminiTimelineSummary = {
  timelineId: string;
  TimelineId?: string;
  modelName: string;
  ModelName?: string;
  aspectRatio?: string;
  AspectRatio?: string;
  imageSize?: string;
  ImageSize?: string;
  lastText?: string;
  LastText?: string;
  lastImageId?: string;
  LastImageId?: string;
  lastImagePath?: string;
  LastImagePath?: string;
  lastImageBase64?: string;
  LastImageBase64?: string;
  lastImageMimeType?: string;
  LastImageMimeType?: string;
  images?: Array<{
    imageId?: string;
    ImageId?: string;
    imagePath?: string;
    ImagePath?: string;
    imageMimeType?: string;
    ImageMimeType?: string;
  }>;
  Images?: Array<{
    imageId?: string;
    ImageId?: string;
    imagePath?: string;
    ImagePath?: string;
    imageMimeType?: string;
    ImageMimeType?: string;
  }>;
  updatedDate?: string;
  UpdatedDate?: string;
};

type GeminiTimelineDetailMessage = {
  role?: "user" | "model" | "system";
  Role?: "user" | "model" | "system";
  text?: string;
  Text?: string;
  imageBase64?: string;
  ImageBase64?: string;
  imageMimeType?: string;
  ImageMimeType?: string;
  images?: Array<{
    imageBase64?: string;
    ImageBase64?: string;
    imageMimeType?: string;
    ImageMimeType?: string;
    imagePath?: string;
    ImagePath?: string;
  }>;
  Images?: Array<{
    imageBase64?: string;
    ImageBase64?: string;
    imageMimeType?: string;
    ImageMimeType?: string;
    imagePath?: string;
    ImagePath?: string;
  }>;
  createdDate?: string;
  CreatedDate?: string;
};

type GeminiTimelineDetailResponse = {
  code?: number;
  Code?: number;
  message?: string;
  Message?: string;
  messages?: GeminiTimelineDetailMessage[];
  Messages?: GeminiTimelineDetailMessage[];
};

type GeminiGenerateApiResponse = {
  code?: number;
  Code?: number;
  message?: string;
  Message?: string;
  timelineId?: string;
  TimelineId?: string;
  text?: string;
  Text?: string;
  imageBase64?: string;
  ImageBase64?: string;
  imageMimeType?: string;
  ImageMimeType?: string;
};

type UnifiedHistoryItem =
  | { type: 'standard'; key: string; sortTs: number; data: GenerationRequest }
  | { type: 'nano'; key: string; sortTs: number; data: GeminiTimelineSummary; imageSrc?: string };

function resolveImagePathToSrc(imagePath?: string): string | undefined {
  if (!imagePath) return undefined;
  if (/^https?:\/\//i.test(imagePath)) return imagePath;
  if (!process.env.REACT_APP_IMAGE_URL) return undefined;
  return `${process.env.REACT_APP_IMAGE_URL}/${imagePath}`;
}

export const GenerationHistoryPage: FC = () => {
  const navigate = useNavigate();
  const MAX_SELECTION = 10;
  const [requestList, setRequestList] = useState<GenerationRequest[] | undefined>(undefined);
  const [nanoTimelineHistory, setNanoTimelineHistory] = useState<GeminiTimelineSummary[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<GenerationRequest | undefined>(undefined);
  const [selectedNanoTimeline, setSelectedNanoTimeline] = useState<GeminiTimelineSummary | undefined>(undefined);
  const [nanoDialogMessages, setNanoDialogMessages] = useState<GeminiTimelineDetailMessage[]>([]);
  const [isNanoDialogLoading, setIsNanoDialogLoading] = useState(false);
  const [nanoDialogTab, setNanoDialogTab] = useState<"basic" | "timeline">("timeline");
  const [nanoDialogExpanded, setNanoDialogExpanded] = useState(false);
  const [nanoFollowupText, setNanoFollowupText] = useState('');
  const [nanoInputImages, setNanoInputImages] = useState<string[]>([]);
  const [isNanoFollowupProcessing, setIsNanoFollowupProcessing] = useState(false);
  const [hasNextData, setHasNextData] = useState(true);
  const [orderValue, setOrderValue] = useState<ComboboxItem>({ value: 'desc', label: '新しい順' });
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [page, setPage] = useState(0);
  const [version, setVersion] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [pendingAction, setPendingAction] = useState<ConfirmActionState | null>(null);
  const nanoFileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (version > 0) {
      if (!isLoggedIn()) return;
      fetchGeneratedList();
    }
  }, [version]);

  async function fetchGeneratedList(){
    var param = { count: 50, page: page, asc: orderValue!.value === 'asc', containsNsfw: false, containsError: false };
    const [list, nanoListRaw] = await Promise.all([
      fetchAsync<GenerationRequest[]>('Generation/GetGeneratedImages', param),
      fetchAsync<GeminiTimelineSummary[]>('Generation/GetGeminiTimelineList', {
        count: 50,
        page: page
      })
    ]);
    const nanoList = (nanoListRaw ?? [])
      .map((item) => ({
        timelineId: item.timelineId ?? item.TimelineId ?? '',
        modelName: item.modelName ?? item.ModelName ?? 'gemini-2.5-flash-image',
        aspectRatio: item.aspectRatio ?? item.AspectRatio,
        imageSize: item.imageSize ?? item.ImageSize,
        lastText: item.lastText ?? item.LastText,
        lastImageId: item.lastImageId ?? item.LastImageId,
        lastImagePath: item.lastImagePath ?? item.LastImagePath,
        lastImageBase64: item.lastImageBase64 ?? item.LastImageBase64,
        lastImageMimeType: item.lastImageMimeType ?? item.LastImageMimeType,
        images: (item.images ?? item.Images ?? []).map((img) => ({
          imageId: img.imageId ?? img.ImageId,
          imagePath: img.imagePath ?? img.ImagePath,
          imageMimeType: img.imageMimeType ?? img.ImageMimeType
        })),
        updatedDate: item.updatedDate ?? item.UpdatedDate
      }))
      .filter((item) => item.timelineId !== '');
  
    // 関数型アップデートを使用してステート更新
    setRequestList((prevRequestList) => {
      if (!prevRequestList || page === 0) {
        return list;
      }
      return prevRequestList.concat(list); // 直前のリストと新しいリストを結合
    });
    setNanoTimelineHistory((prev) => {
      if (page === 0) return nanoList;
      return prev.concat(nanoList);
    });

    if(list.length === 0 && nanoList.length === 0) {
      setHasNextData(false); // 次のデータがない場合の処理
    }
  };

  function buildUnifiedHistoryItems(): UnifiedHistoryItem[] {
    const standardItems: UnifiedHistoryItem[] = (requestList ?? []).map((item) => ({
      type: 'standard',
      key: `standard-${item.requestId}`,
      sortTs: item.createdDate ? new Date(item.createdDate).getTime() : 0,
      data: item
    }));

    const nanoItems: UnifiedHistoryItem[] = nanoTimelineHistory.flatMap((item) => {
      const sortTs = item.updatedDate ? new Date(item.updatedDate).getTime() : 0;
      const imagePaths = (item.images ?? [])
        .map((img) => img.imagePath)
        .filter((path): path is string => !!path);

      if (imagePaths.length > 0) {
        return imagePaths.map((imagePath, index) => ({
          type: 'nano' as const,
          key: `nano-${item.timelineId}-${index}`,
          sortTs,
          data: item,
          imageSrc: resolveImagePathToSrc(imagePath)
        }));
      }

      const fallbackImageSrc = resolveImagePathToSrc(item.lastImagePath)
        ?? (item.lastImageBase64 && item.lastImageMimeType
          ? `data:${item.lastImageMimeType};base64,${item.lastImageBase64}`
          : undefined);

      if (!fallbackImageSrc) {
        return [];
      }

      return [{
        type: 'nano' as const,
        key: `nano-${item.timelineId}-fallback`,
        sortTs,
        data: item,
        imageSrc: fallbackImageSrc
      }];
    });

    const merged = [...standardItems, ...nanoItems];
    if (orderValue.value === 'asc') {
      merged.sort((a, b) => a.sortTs - b.sortTs);
    } else {
      merged.sort((a, b) => b.sortTs - a.sortTs);
    }
    return merged;
  }

  function toggleSelectionMode() {
    setIsSelectionMode((prev) => {
      if (prev) {
        setSelectedIds([]);
      }
      return !prev;
    });
  }

  function toggleImageSelection(requestId: string) {
    setSelectedIds((prev) => {
      if (prev.includes(requestId)) {
        return prev.filter((id) => id !== requestId);
      }
      return [...prev, requestId];
    });
  }

  function openConfirm(action: 'delete' | 'download', targetIds?: string[], origin: 'selection' | 'detail' = 'selection') {
    const ids = targetIds ?? selectedIds;
    if (ids.length === 0) return;
    if (action === 'download' && ids.length > MAX_SELECTION) {
      alert(`ダウンロードは一度に最大${MAX_SELECTION}件までです。`);
      return;
    }
    setPendingAction({ type: action, ids, origin });
  }

  function closeConfirm() {
    setPendingAction(null);
  }

  async function handleDelete(ids: string[], origin: 'selection' | 'detail') {
    if (ids.length === 0 || isActionLoading) return;
    setIsActionLoading(true);
    try {
      const response = await postAsync<string>('Generation/DeleteImages', { ids });
      if ((response as any)?.code === 1) {
        setRequestList((prev) => prev ? prev.filter((item) => !ids.includes(item.requestId)) : prev);
        if (origin === 'selection') {
          setSelectedIds([]);
          setIsSelectionMode(false);
        } else {
          setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
        }
        setSelectedRequest((prev) => (prev && ids.includes(prev.requestId) ? undefined : prev));
      } else if ((response as any)?.message) {
        alert((response as any).message);
      }
    } catch (error) {
      console.error(error);
      alert('削除に失敗しました。時間をおいて再度お試しください。');
    } finally {
      setIsActionLoading(false);
    }
  }

  async function handleDownload(ids: string[]) {
    if (ids.length === 0 || isDownloading) return;
    setIsDownloading(true);
    try {
      const token = getToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/ZipDownload/Download`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ ids })
      });
      if (!response.ok) {
        throw new Error('ダウンロードに失敗しました');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `generated-images-${dayjs().format('YYYYMMDD-HHmmss')}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      alert('ダウンロードに失敗しました。時間をおいて再度お試しください。');
    } finally {
      setIsDownloading(false);
    }
  }

  function handleConfirmOk() {
    if (!pendingAction) return;
    const action = pendingAction;
    closeConfirm();
    if (action.type === 'delete') {
      handleDelete(action.ids, action.origin);
    } else if (action.type === 'download') {
      handleDownload(action.ids);
    }
  }

  function handleUsePrompt(request: GenerationRequest) {
    const prompt = `${request.prompt ?? ''} ${request.additionalPrompt ?? ''}`.trim();
    const negativePrompt = `${request.negativePrompt ?? ''} ${request.additionalNegativePrompt ?? ''}`.trim();
    setSelectedRequest(undefined);
    navigate('/image-generation', {
      state: {
        prefillPrompt: prompt,
        prefillNegativePrompt: negativePrompt,
        prefillTasteName: request.tasteName,
      }
    });
  }

  function handleUseAsReferenceImage(request: GenerationRequest) {
    setSelectedRequest(undefined);
    navigate('/image-generation', {
      state: {
        prefillReferenceRequestId: request.requestId
      }
    });
  }

  async function openNanoDialog(item: GeminiTimelineSummary) {
    setSelectedNanoTimeline(item);
    setNanoDialogMessages([]);
    setNanoFollowupText('');
    setNanoInputImages([]);
    setNanoDialogTab('timeline');
    setNanoDialogExpanded(false);
    setIsNanoDialogLoading(true);
    try {
      const response = await fetchAsync<GeminiTimelineDetailResponse>('Generation/GetGeminiTimelineDetail', {
        timelineId: item.timelineId,
        includeImageBase64: false
      });
      const messages = response.messages ?? response.Messages ?? [];
      setNanoDialogMessages(messages);
    } catch {
      setNanoDialogMessages([]);
    } finally {
      setIsNanoDialogLoading(false);
    }
  }

  function closeNanoDialog() {
    if (nanoDialogExpanded) {
      setNanoDialogExpanded(false);
      return;
    }
    setNanoDialogExpanded(false);
    setSelectedNanoTimeline(undefined);
  }

  async function handleNanoFollowupGenerate() {
    if (!selectedNanoTimeline) return;
    const text = nanoFollowupText.trim();
    if ((text === '' && nanoInputImages.length === 0) || isNanoFollowupProcessing) return;

    setIsNanoFollowupProcessing(true);
    try {
      const response = await postAsync<any>('Generation/GenerateGeminiImage', {
        timelineId: selectedNanoTimeline.timelineId,
        modelName: selectedNanoTimeline.modelName,
        text: text || undefined,
        imageBase64List: nanoInputImages.length > 0 ? nanoInputImages : undefined,
        imageBase64: nanoInputImages.length > 0 ? nanoInputImages[0] : undefined,
      }) as GeminiGenerateApiResponse;

      const responseCode = response.code ?? response.Code;
      if (responseCode !== 1) {
        alert(response.message ?? response.Message ?? '生成に失敗しました。');
        return;
      }

      const responseText = response.text ?? response.Text;
      const responseImageBase64 = response.imageBase64 ?? response.ImageBase64;
      const responseImageMimeType = response.imageMimeType ?? response.ImageMimeType;
      const timelineId = selectedNanoTimeline.timelineId;
      const responseImageDataUrl = responseImageBase64 && responseImageMimeType
        ? `data:${responseImageMimeType};base64,${responseImageBase64}`
        : undefined;

      const updatedItem: GeminiTimelineSummary = {
        ...selectedNanoTimeline,
        lastText: responseText ?? selectedNanoTimeline.lastText,
        lastImagePath: undefined,
        lastImageId: undefined,
        lastImageBase64: responseImageBase64 ?? selectedNanoTimeline.lastImageBase64,
        lastImageMimeType: responseImageMimeType ?? selectedNanoTimeline.lastImageMimeType,
        updatedDate: new Date().toISOString()
      };

      setNanoTimelineHistory((prev) => prev.map((item) => (
        item.timelineId === timelineId ? updatedItem : item
      )));
      setSelectedNanoTimeline(updatedItem);
      setNanoDialogMessages((prev) => [
        ...prev,
        {
          role: "user",
          text: text || undefined,
          images: nanoInputImages.length > 0
            ? nanoInputImages
                .map((url) => {
                  const base64Index = url.indexOf("base64,");
                  if (base64Index < 0) return undefined;
                  const mimeStart = url.indexOf("data:");
                  const mimeEnd = url.indexOf(";base64,");
                  if (mimeStart !== 0 || mimeEnd < 0) return undefined;
                  return {
                    imageBase64: url.substring(base64Index + 7),
                    imageMimeType: url.substring(5, mimeEnd)
                  };
                })
                .filter((e): e is { imageBase64: string; imageMimeType: string } => !!e)
            : undefined,
          createdDate: new Date().toISOString()
        },
        {
          role: "model",
          text: responseText ?? '生成が完了しました。',
          images: responseImageDataUrl
            ? [{
                imageBase64: responseImageBase64,
                imageMimeType: responseImageMimeType
              }]
            : undefined,
          createdDate: new Date().toISOString()
        }
      ]);
      setNanoFollowupText('');
      setNanoInputImages([]);
    } catch {
      alert('生成に失敗しました。時間をおいて再度お試しください。');
    } finally {
      setIsNanoFollowupProcessing(false);
    }
  }

  async function onChangeNanoInputImages(files: FileList | null) {
    if (!files || files.length === 0) return;
    const dataUrls = await Promise.all(
      Array.from(files).map((file) =>
        new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve((reader.result as string) ?? "");
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(file);
        })
      )
    );
    setNanoInputImages((prev) => {
      const merged = [...prev, ...dataUrls.filter((e) => e !== "")];
      return merged.slice(Math.max(0, merged.length - 14));
    });
    if (nanoFileInputRef.current) {
      nanoFileInputRef.current.value = "";
    }
  }

  function removeNanoInputImage(index: number) {
    setNanoInputImages((prev) => prev.filter((_, i) => i !== index));
  }

  async function getNextData(){
    if(isProcessing) {
      return;
    }

    if(hasNextData === false){
      return;
    }

    setPage(prevPage => prevPage + 1);
    setVersion(prevVersion => prevVersion + 1);
  }

  useEffect(() => {
    // fetchGeneratedList();
    listenScrollEvent();
  }, [])

  useEffect(() => {
    setRequestList([]);
    setNanoTimelineHistory([]);
    setPage(0);
    setVersion(prevVersion => prevVersion + 1);
    setHasNextData(true);

  }, [orderValue])

  function listenScrollEvent() {
    window.addEventListener('scroll', function() {
      const windowHeight = window.innerHeight;
      const scrollTop = window.scrollY;
      const documentHeight = document.body.scrollHeight;
      if (windowHeight + scrollTop >= documentHeight - 20) {
        getNextData();
      }
    });
  }

  function buildDialog(){
    return <>
    <div id="processing-dialog" className="dialog-overlay">
      <div className='text-start max-h-[80vh] overflow-scroll w-[95%] sm:w-[90%] lg:w-[1200px] bg-white p-5 rounded-lg shadow-lg' style={{ maxWidth: "1400px", paddingTop: '0px' }}>
        <div className="pb-10 pt-4 bg-white" style={{ position: 'sticky', top: '0px'}}>
          <div className="mb-2 text-lg flex justify-end absolute w-full">
            <img onClick={(e) => setSelectedRequest(undefined)} src="/images/icon-close.svg" className="clickable w-[32px]"></img>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row">
          <div className="w-full sm:w-[50%] flex items-center sm:items-start min-h-[200px] sm:min-h-[500px] justify-center sm:justify-start">
            <div className="relative w-full max-w-[600px] px-4 sm:px-0">
              <img className="w-full" src={`${process.env.REACT_APP_IMAGE_URL}/${selectedRequest!.requestId}`} />
              <button
                type="button"
                onClick={() => handleUseAsReferenceImage(selectedRequest!)}
                className="absolute right-3 bottom-3 w-11 h-11 rounded-full bg-white/95 border border-[#D7DCEF] shadow-md flex items-center justify-center clickable"
                title="参照画像として使う"
              >
                <img src={iconImageAdd} alt="参照画像として使う" className="w-5 h-5 object-contain" />
              </button>
            </div>
          </div>
          <div className="w-full sm:w-[50%] px-4 sm:ml-4 sm:px-0 pb-4">
            <div className="text-sm">生成日時</div>
            <div className="text-sm border py-1 px-2 rounded">
              { selectedRequest!.createdDate ? dayjs(selectedRequest!.createdDate).format('YYYY-M-D H:mm') : '' }
            </div>
            <div className="text-sm">モデル</div>
            <div className="text-sm border py-1 px-2 rounded">
              { selectedRequest!.tasteName }
            </div>
            <div className="text-sm mt-2">シード値</div>
            <div className="text-sm border py-1 px-2 rounded">
              { selectedRequest!.seed }
            </div>
            <div className="text-sm mt-2">ステップ数</div>
            <div className="text-sm border py-1 px-2 rounded">
              { selectedRequest!.inferenceSteps }
            </div>
            <div className="text-sm mt-2">スケール</div>
            <div className="text-sm border py-1 px-2 rounded">
              { selectedRequest!.guidanceScale }
            </div>
            <div className="text-sm mt-2">アスペクト比</div>
            <div className="text-sm border py-1 px-2 rounded">
              { `${selectedRequest!.aspectRatio} (${selectedRequest!.width}px x ${selectedRequest!.height}px)` }
            </div>
            <div className="text-sm mr-4 mt-4">プロンプト</div>
            <div className="text-sm border py-1 px-2 min-h-7 rounded">
              { `${selectedRequest!.prompt} ${selectedRequest!.additionalPrompt}` }
            </div>
            <>
            <div className="text-sm mt-4">ネガティブプロンプト</div>
            <div className="text-sm border py-1 px-2 min-h-7 rounded">
              { `${selectedRequest!.negativePrompt} ${selectedRequest!.additionalNegativePrompt}` }
            </div>
            </>
            <div className="mt-6 space-y-3">
              <button
                className="w-full gradation-button py-2 px-4 rounded font-bold clickable"
                onClick={() => handleUsePrompt(selectedRequest!)}
              >
                このプロンプトを利用する
              </button>
              <button
                className="w-full text-sm font-semibold text-red-600 py-2"
                onClick={() => openConfirm('delete', [selectedRequest!.requestId], 'detail')}
              >
                この画像を削除する
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </>;
  }

  function buildNanoDialog() {
    if (!selectedNanoTimeline) return <></>;
    const isSp = window.innerWidth < 640;

    const modelLabel = selectedNanoTimeline.modelName === 'gemini-3-pro-image-preview'
      ? 'Nano Banana Pro'
      : 'Nano Banana';
    const followupModelIconSrc = selectedNanoTimeline.modelName === 'stable-diffusion-xl'
      ? '/images/stable-diffusion.png'
      : bananaIcon;
    const followupModelIconAlt = modelLabel;
    const normalizedMessages = nanoDialogMessages.map((msg) => {
      const role = msg.role ?? msg.Role ?? "model";
      const text = msg.text ?? msg.Text;
      const imageBase64 = msg.imageBase64 ?? msg.ImageBase64;
      const imageMimeType = msg.imageMimeType ?? msg.ImageMimeType;
      const imageList = msg.images ?? msg.Images ?? [];
      const imageDataUrls = imageList
        .map((img) => {
          const b64 = img.imageBase64 ?? img.ImageBase64;
          const mime = img.imageMimeType ?? img.ImageMimeType;
          if (b64 && mime) return `data:${mime};base64,${b64}`;
          const imagePath = img.imagePath ?? img.ImagePath;
          const imageSrc = resolveImagePathToSrc(imagePath);
          if (imageSrc) return imageSrc;
          return "";
        })
        .filter((e) => e !== "");
      const singleImageDataUrl = imageBase64 && imageMimeType
        ? `data:${imageMimeType};base64,${imageBase64}`
        : undefined;
      const mergedImages = imageDataUrls.length > 0
        ? imageDataUrls
        : (singleImageDataUrl ? [singleImageDataUrl] : []);
      const createdDate = msg.createdDate ?? msg.CreatedDate;
      return {
        role,
        text,
        imageDataUrls: mergedImages,
        createdAt: createdDate ? new Date(createdDate).getTime() : 0
      };
    });
    const generatedImages = normalizedMessages
      .filter((e) => e.role === "model")
      .flatMap((e) => e.imageDataUrls);
    const fallbackImageSrc = resolveImagePathToSrc(selectedNanoTimeline.lastImagePath)
      ?? (selectedNanoTimeline.lastImageBase64 && selectedNanoTimeline.lastImageMimeType
        ? `data:${selectedNanoTimeline.lastImageMimeType};base64,${selectedNanoTimeline.lastImageBase64}`
        : undefined);
    const galleryImages = generatedImages.length > 0
      ? generatedImages
      : (fallbackImageSrc ? [fallbackImageSrc] : []);
    const lastUserText = [...normalizedMessages]
      .reverse()
      .find((e) => e.role === "user" && (e.text ?? "").trim() !== "")?.text
      ?? selectedNanoTimeline.lastText
      ?? "";
    const formatDateTime = (value?: string) => {
      if (!value) return "-";
      return dayjs(value).format('YYYY/M/D HH:mm');
    };
    const renderBasicInfoRow = (label: string, value?: string) => (
      <div className="mt-4 first:mt-0">
        <div className="text-sm font-bold text-[#050B5A]">{label}</div>
        <div className="mt-1 text-sm leading-[1.5] text-[#4E556E] whitespace-pre-wrap break-words">
          {value && value.trim() !== "" ? value : "-"}
        </div>
      </div>
    );

    return <>
      <div id="processing-dialog" className="dialog-overlay">
        <div
          className={`text-start overflow-y-auto overflow-x-hidden w-full bg-white shadow-lg ${
            (nanoDialogExpanded || isSp)
              ? "fixed inset-0 h-[100dvh] max-h-none rounded-none p-3"
              : "max-h-[80vh] p-5 rounded-lg sm:h-[80vh] sm:overflow-hidden sm:flex sm:flex-col"
          }`}
          style={{
            maxWidth: (nanoDialogExpanded || isSp) ? "100%" : "960px",
            minHeight: (nanoDialogExpanded || isSp) ? "100dvh" : "520px",
            boxSizing: "border-box"
          }}
        >
          <div className="pb-10 pt-1 bg-white" style={{ position: 'sticky', top: '0px' }}>
            <div className="mb-2 text-lg flex justify-end absolute w-full">
              <img onClick={closeNanoDialog} src="/images/icon-close.svg" className="clickable w-[32px]" />
            </div>
          </div>
          <div className="border-b pb-2"></div>
          <div className={`mt-3 ${nanoDialogExpanded ? "" : "sm:grid sm:grid-cols-2 sm:gap-4 sm:items-stretch sm:flex-1 sm:min-h-0"}`}>
            <div className={`${nanoDialogExpanded ? "hidden" : "sm:flex sm:flex-col min-h-0"}`}>
              <div className="text-base font-bold text-[#1F2352] mb-2">生成した画像</div>
              <div className="flex flex-wrap -mx-1 items-start content-start flex-1 min-h-0 overflow-y-auto pr-1">
                {galleryImages.map((src, index) => (
                  <div key={`nano-history-generated-image-${index}`} className="w-1/2 px-1 mb-2">
                    <div className="w-full" style={{ position: "relative", borderRadius: "0.5rem", border: "1px solid #E2E5F3", overflow: "hidden" }}>
                      <div style={{ width: "100%", paddingTop: "100%" }}></div>
                      <img
                        src={src}
                        alt={`生成画像 ${index + 1}`}
                        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                      />
                    </div>
                  </div>
                ))}
                {isNanoFollowupProcessing && (
                  <div className="w-1/2 px-1 mb-2">
                    <div
                      className="w-full"
                      style={{ position: "relative", borderRadius: "0.5rem", border: "1px dashed #C7CCE4", background: "#F8F9FF", overflow: "hidden" }}
                    >
                      <div style={{ width: "100%", paddingTop: "100%" }}></div>
                      <div className="flex flex-col items-center justify-center text-xs text-[#3D4677]" style={{ position: "absolute", inset: 0 }}>
                        <Loader type="dots" size="14px" />
                        <span className="mt-1">画像生成中</span>
                      </div>
                    </div>
                  </div>
                )}
                {galleryImages.length === 0 && !isNanoFollowupProcessing && (
                  <div className="w-full px-1 text-xs text-[#8A90A7] py-2">まだ画像は生成されていません</div>
                )}
              </div>
            </div>

            <div className={`min-h-0 ${nanoDialogExpanded ? "h-full flex flex-col" : "sm:h-full sm:flex sm:flex-col"}`}>
              <div className="mt-0 sm:mt-0 relative flex justify-center">
                <div className="inline-flex rounded-lg bg-[#F1F3FF] p-1 gap-1">
                  <button
                    className={`px-4 py-1 text-sm rounded-md ${nanoDialogTab === "basic" ? "bg-white text-[#0909FF]" : "text-[#5C5C7A]"}`}
                    onClick={() => setNanoDialogTab("basic")}
                  >
                    基本情報
                  </button>
                  <button
                    className={`px-4 py-1 text-sm rounded-md ${nanoDialogTab === "timeline" ? "bg-white text-[#0909FF]" : "text-[#5C5C7A]"}`}
                    onClick={() => setNanoDialogTab("timeline")}
                  >
                    タイムライン
                  </button>
                </div>
                {!nanoDialogExpanded && (
                  <button
                    type="button"
                    className="sm:hidden absolute right-0 top-1/2 -translate-y-1/2 w-8 h-8 rounded-md bg-white flex items-center justify-center clickable"
                    onClick={() => {
                      setNanoDialogExpanded(true);
                      setNanoDialogTab("timeline");
                    }}
                    title="画像エリアを非表示"
                  >
                    <img src="/images/icon/icons8-expand.png" alt="expand" className="w-4 h-4" />
                  </button>
                )}
              </div>

              {nanoDialogTab === "basic" && (
                <div className="mt-8 px-3 sm:px-4 sm:flex-1 sm:min-h-0 sm:overflow-y-auto">
                  {renderBasicInfoRow("生成日時", formatDateTime(selectedNanoTimeline.updatedDate))}
                  {renderBasicInfoRow("生成モデル", modelLabel)}
                  {renderBasicInfoRow("アスペクト比", selectedNanoTimeline.aspectRatio)}
                  {selectedNanoTimeline.modelName === "gemini-3-pro-image-preview" && renderBasicInfoRow("画像サイズ", selectedNanoTimeline.imageSize)}
                  {renderBasicInfoRow("プロンプト", lastUserText)}
                </div>
              )}

              {nanoDialogTab === "timeline" && (
                <div className={`${nanoDialogExpanded ? "mt-2" : "mt-4"} space-y-3 flex-1 min-h-0 overflow-y-auto sm:pr-1`}>
                  <div className="flex justify-center">
                    <div className="flex items-center gap-2 py-1 px-3 bg-[#F3F4F8] rounded-2xl">
                      <img src={bananaIcon} alt={modelLabel} className="w-5 h-5 object-contain" />
                      <div className="text-sm text-[#1F2352]">{modelLabel}</div>
                    </div>
                  </div>
                  {isNanoDialogLoading && (
                    <div className="flex flex-col items-center justify-center gap-1 py-6">
                      <Loader type="dots" />
                      <span className="text-sm text-[#1F2352]">会話履歴を読み込み中</span>
                    </div>
                  )}
                  {!isNanoDialogLoading && normalizedMessages.map((item, index) => {
                    const isUser = item.role === "user";
                    const isSystem = item.role === "system";
                    const containerClass = isSystem
                      ? "bg-[#FFF7F7] border-[#FFD6D6]"
                      : isUser
                        ? "bg-[#EEF2FF] border-[#D9E2FF]"
                        : "bg-[#F8F6FF] border-[#E4DCF8]";
                    const rowClass = isSystem
                      ? "flex justify-center"
                      : isUser
                        ? "flex justify-end"
                        : "flex justify-start";
                    const widthClass = isSystem ? "w-full" : "w-[85%] sm:w-[80%]";
                    return (
                      <div key={`history-timeline-item-${index}`} className={rowClass}>
                        <div className={`rounded-xl p-3 border ${containerClass} ${widthClass}`}>
                          {item.text && <div className="text-sm whitespace-pre-wrap text-[#1F2352]">{item.text}</div>}
                          {item.imageDataUrls.length > 0 && (
                            <div className={`mt-2 grid ${item.imageDataUrls.length === 1 ? "grid-cols-1" : "grid-cols-2"} gap-2`}>
                              {item.imageDataUrls.map((src, imageIndex) => (
                                <img
                                  key={`history-timeline-item-${index}-image-${imageIndex}`}
                                  src={src}
                                  className="w-full rounded-lg border border-[#E2E5F3]"
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {isNanoFollowupProcessing && (
                    <div className="flex flex-col items-center justify-center gap-1 py-6">
                      <Loader type="dots" />
                      <span className="text-sm text-[#1F2352]">画像生成中</span>
                    </div>
                  )}
                </div>
              )}

              {nanoDialogTab === "timeline" && (
                <div className="mt-4 pt-3 border-t border-[#E6E8F0]">
                  <div className="flex items-center gap-2">
                    <input
                      ref={nanoFileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => onChangeNanoInputImages(e.target.files)}
                    />
                    <button
                      type="button"
                      onClick={() => nanoFileInputRef.current?.click()}
                      disabled={isNanoFollowupProcessing}
                      className="w-8 h-8 rounded-lg border border-gray-200 bg-white flex items-center justify-center clickable relative overflow-hidden shrink-0 disabled:opacity-50"
                      title="画像を追加"
                    >
                      <img src={iconImageAdd} alt="画像追加" className="w-4 h-4" />
                    </button>
                    <div className="flex-1 relative">
                      <img
                        src={followupModelIconSrc}
                        alt={followupModelIconAlt}
                        className="w-5 h-5 object-contain absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                      />
                      <input
                        type="text"
                        value={nanoFollowupText}
                        onChange={(e) => setNanoFollowupText(e.target.value)}
                        onKeyDown={async (e) => {
                          if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                            e.preventDefault();
                            await handleNanoFollowupGenerate();
                          }
                        }}
                        disabled={isNanoFollowupProcessing}
                        placeholder="生成したい画像の指示を入力"
                        className="w-full border border-[#CDD3EA] rounded-lg pl-10 pr-3 py-2 text-sm outline-none focus:border-[#8A92F8]"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleNanoFollowupGenerate}
                      disabled={isNanoFollowupProcessing || (nanoFollowupText.trim() === '' && nanoInputImages.length === 0)}
                      className="w-10 h-10 rounded-lg text-sm text-white disabled:opacity-50 flex items-center justify-center shrink-0"
                      style={{ background: "linear-gradient(91.51deg, #0000F3 0.07%, #F416BF 100.07%)" }}
                    >
                      <img src="/images/flare.png" alt="生成" className="w-5 h-5" />
                    </button>
                  </div>
                  {nanoInputImages.length > 0 && (
                    <div className="mt-2 flex items-center gap-2 overflow-x-auto pb-1">
                      {nanoInputImages.map((img, index) => (
                        <div
                          key={`nano-input-image-${index}`}
                          className="relative shrink-0 w-8 h-8 rounded-lg overflow-hidden border border-[#D7DCEF] bg-white"
                        >
                          <img src={img} alt={`選択画像${index + 1}`} className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeNanoInputImage(index);
                            }}
                            className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-white border border-gray-300 flex items-center justify-center"
                            aria-label="画像を削除"
                          >
                            <img src="/images/icon-close.svg" alt="削除" className="w-2 h-2" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>;
  }

  return <>
    <Helmet>
      <title>生成履歴 - Drop AI</title>
      <meta name="description" content="生成履歴でお客さまの画像生成履歴を確認いただけます。" />
      <meta property="og:url" content="https://drop4i.com/generation-history" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="生成履歴 - Drop AI | 無料で使えるAI画像生成ツール" />
      <meta property="og:description" content="生成履歴でお客さまの画像生成履歴を確認いただけます。" />
      <style>{`
        .history-grid {
          display: grid !important;
          grid-template-columns: repeat(2, 1fr) !important;
          gap: 8px;
        }
        @media (min-width: 640px) {
          .history-grid {
            grid-template-columns: repeat(4, 1fr) !important;
          }
        }
        @media (min-width: 768px) {
          .history-grid {
            grid-template-columns: repeat(5, 1fr) !important;
          }
        }
      `}</style>
    </Helmet>
    { selectedRequest && buildDialog() }
    { selectedNanoTimeline && buildNanoDialog() }
    <div className="max-h-[calc(100vh-76px)] sm:max-h-[calc(100vh-76px)] overflow-scroll relative pb-16 sm:pb-0">
      <div className="flex items-center mb-1 mt-4 justify-between sm:justify-start">
        <div className="text-[24px] sm:text-[30px] ml-3 sm:ml-0">生成履歴</div>
        <div className="flex items-center ml-2 sm:ml-6">
          <button
            onClick={toggleSelectionMode}
            className="px-4 py-2 text-sm rounded border bg-white text-gray-800"
          >
            {isSelectionMode ? 'キャンセル' : '選択'}
          </button>
          <Select
            className="ml-2 mr-10 sm:mr-0"
            size="sm"
            styles={{ input: { minWidth: '110px', width: '110px', paddingLeft: '8px', paddingRight: '24px' } }}
            data={[
              { value: 'asc', label: '古い順' },
              { value: 'desc', label: '新しい順' }
            ]}
            value={orderValue ? orderValue.value : null}
            onChange={(_value, option) => setOrderValue(option)}
          />
          {/* Mobile close button */}
          <button 
            onClick={() => navigate(-1)} 
            className="sm:hidden absolute top-4 right-0.5 p-2 clickable"
          >
            <img src="/images/icon-close.svg" className="w-[24px] h-[24px]" alt="閉じる" />
          </button>
        </div>
      </div>
      {isSelectionMode &&
        <div className="text-xs text-gray-500 ml-3 sm:ml-0 mb-1">
          ダウンロードは一度に最大{MAX_SELECTION}件まで選択できます。
        </div>
      }
      { requestList === undefined &&
        <div className="min-h-[calc(100vh-76px)] h-[900px]"> 
        </div>
      }
      { requestList !== undefined &&
      <div className="history-grid min-h-[calc(100vh-76px)] mt-2 m-auto max-w-full sm:max-w-[1200px] w-full sm:w-[1200px] bg-white p-1 sm:p-0"> 
        { buildUnifiedHistoryItems().map((item) => {
          if (item.type === 'standard') {
            const e = item.data;
            return (
              <div
                key={item.key}
                className={`relative ${isSelectionMode && selectedIds.includes(e.requestId) ? 'ring-4 ring-blue-500 rounded-lg' : ''}`}
                onClick={() => {
                  if (isSelectionMode) {
                    toggleImageSelection(e.requestId);
                  } else {
                    setSelectedRequest(e);
                  }
                }}
              >
                {isSelectionMode &&
                  <div className="absolute top-2 right-2 z-10 pointer-events-none bg-white rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold text-gray-800 border border-gray-300 shadow">
                    {selectedIds.includes(e.requestId) ? '✓' : ''}
                  </div>
                }
                <img
                  className="w-full sm:max-w-[240px] rounded-lg cursor-pointer transition-opacity duration-150 hover:opacity-70"
                  src={`${process.env.REACT_APP_IMAGE_URL}/${e.requestId}_mid`}
                />
              </div>
            );
          }

          const n = item.data;
          const imageSrc = item.imageSrc;

          return (
            <div key={item.key} className="relative">
              <div
                className="w-full aspect-square rounded-lg border border-[#D9DDF2] bg-[#F5F7FF] overflow-hidden clickable"
                onClick={() => {
                  if (isSelectionMode) return;
                  setSelectedRequest(undefined);
                  openNanoDialog(n);
                }}
              >
                {imageSrc ? (
                  <img
                    className="w-full h-full object-cover"
                    src={imageSrc}
                    alt="Nano Banana 生成画像"
                  />
                ) : (
                  <div className="w-full h-full text-xs text-[#4A5580] flex items-center justify-center px-2 text-center">
                    {n.lastText ?? 'タイムライン'}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        </div>
      }
    </div>
    {isSelectionMode &&
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg p-4 flex justify-center gap-6 z-20">
        <button
          aria-label="削除"
          className="p-3 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-40 flex items-center justify-center"
          disabled={selectedIds.length === 0 || isActionLoading}
          onClick={() => openConfirm('delete')}
        >
          <img src={TrashIcon} alt="削除" className="w-5 h-5" />
        </button>
        <button
          aria-label="ダウンロード"
          className="p-3 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-40 flex items-center justify-center relative"
          disabled={selectedIds.length === 0 || isDownloading}
          onClick={() => openConfirm('download')}
        >
          <img src={DownloadIcon} alt="ダウンロード" className={`w-5 h-5 ${isDownloading ? 'opacity-0' : 'opacity-100'}`} />
          {isDownloading &&
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader size="xs" color="dark" />
            </div>
          }
        </button>
      </div>
    }
    {pendingAction &&
      <div className="fixed inset-0 bg-black/40 z-[200] flex items-center justify-center px-4 py-8">
        <div className="w-full sm:max-w-[360px]">
          <div className="bg-[#f2f2f7] rounded-2xl text-center shadow-lg overflow-hidden">
            <div className="bg-white px-4 py-6 text-sm text-gray-800 whitespace-pre-line">
              {pendingAction.type === 'download'
                ? `選択した${pendingAction.ids.length}件の画像をダウンロードしますか？`
                : pendingAction.origin === 'detail'
                  ? '削除した生成履歴は復元できません。\n削除しますか？'
                  : `選択した${pendingAction.ids.length}件の画像を削除しますか？\nなお、削除した画像は復元できません。`}
            </div>
            <div className="border-t border-gray-200 flex">
              <button
                className="flex-1 py-3 text-blue-500 text-base font-semibold"
                onClick={closeConfirm}
              >
                キャンセル
              </button>
            </div>
            <div className="border-t border-gray-200 flex">
              <button
                className="flex-1 py-3 text-blue-500 text-base font-semibold disabled:opacity-40"
                onClick={handleConfirmOk}
                disabled={(pendingAction.type === 'delete' && isActionLoading) || (pendingAction.type === 'download' && isDownloading)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      </div>
    }
    <FooterMenu />
  </>;
}
  
